import { memo, useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@betty/i18n';
import { LobbyWidget, Timer, Text, Coins } from '@betty/moonlight';
import {
  useNavigation,
  StackNavigationProp,
  BottomSheet,
} from '@betty/navigation';
import useDailyScratcher from '../../hooks/useDailyScratcher';
import { resetAnimatedCoins, selectShouldShowAnimatedCoins } from '../../state';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { coinBalanceWallet } from '@betty/wallet';

function DailyScratcherWidget() {
  const dispatch = useDispatch();
  const { t } = useTranslation('dailyScratcher');
  const shouldShowAnimatedCoins = useSelector(selectShouldShowAnimatedCoins);
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const { dailyScratcher, dailyScratcherStatus, isVisible, fetch } =
    useDailyScratcher();

  const widgetRef = useRef<View>(null);

  useEffect(() => {
    const canAnimate =
      shouldShowAnimatedCoins && !!dailyScratcher?.rewardAmount;
    if (!canAnimate) return;

    const raf = requestAnimationFrame(() => {
      const targetElement = coinBalanceWallet.current;
      const widget = widgetRef.current;

      if (!targetElement || !widget) return;

      widget.measureInWindow((wx, wy, wWidth) => {
        targetElement.measureInWindow((tx, ty, tWidth, tHeight) => {
          const entryX = wx + (wWidth || 0) / 2;
          const entryY = wy - 40;

          Coins.animate({
            count: 40,
            duration: 1500,
            spawnRange: 100,
            entryPoint: { x: entryX, y: entryY },
            targetPoint: { x: tx + tWidth / 2, y: ty + tHeight / 2 },
          });

          setTimeout(() => {
            dispatch(resetAnimatedCoins());
          }, 1600);
        });
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [shouldShowAnimatedCoins, dailyScratcher?.rewardAmount, dispatch]);

  const onClick = useCallback(() => {
    if (dailyScratcherStatus !== 'Awarded') {
      navigation.navigate('DailyScratcher');
    } else {
      BottomSheet.open({
        namespace: 'dailyScratcher',
        type: 'DailyScratcherAwardedBottomSheet',
        light: true,
        data: {
          rewardAmount: dailyScratcher?.rewardAmount,
          nextAvailableAt: dailyScratcher?.nextAvailableAt,
          handleGoToShop: () => {
            navigation.navigate('Shop');
          },
        },
      });
    }
  }, [navigation, dailyScratcher, dailyScratcherStatus]);

  if (!isVisible) {
    return null;
  }

  const timer = (
    <>
      <Text variant="caption-semi" color="darkSurfacePrimary">
        {dailyScratcherStatus === 'Awarded' ? t('claimIn') : t('claimNow')}
      </Text>
      {dailyScratcherStatus === 'Awarded' &&
        dailyScratcher?.nextAvailableAt && (
          <Timer
            variant="caption-semi"
            color="darkSurfacePrimary"
            time={dailyScratcher.nextAvailableAt}
            onCountDownComplete={fetch}
            displayMode="condensed"
          />
        )}
    </>
  );

  return (
    <View ref={widgetRef}>
      <LobbyWidget
        image="dailyScratcher.scratchCard"
        name="common.Hourglass"
        label={t('widgetLabel')}
        hasUnread={dailyScratcherStatus === 'Pending'}
        timer={timer}
        onClick={onClick}
      />
    </View>
  );
}

export default memo(DailyScratcherWidget);
